import { useEffect, useMemo, useState } from "react";
import * as TablerIcons from "@tabler/icons-react";
import * as LucideIcons from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchAdminMedia, uploadAdminMedia } from "@/admin/api";

export type IconItem = {
  name: string;
  value: string;
  isClass?: boolean;
  component?: any;
};

// Curated list of high-quality Child Development themed icons using Remix Icons
const childDevIcons: IconItem[] = [
  // Developmental Pillars & Core
  { name: "Baby / Child (طفل رضيع)", value: "ri-baby-line", isClass: true },
  { name: "Child Carriage / Stroller (عربة أطفال)", value: "ri-baby-carriage-line", isClass: true },
  { name: "Parenting / Family (الوالدين والعائلة)", value: "ri-parent-line", isClass: true },
  { name: "Heart (Love / Care) (قلب / رعاية)", value: "ri-heart-line", isClass: true },
  { name: "Heart Pulse (Health / Growth) (نبض / صحة حركية)", value: "ri-heart-pulse-line", isClass: true },
  { name: "Hand Heart (Empathy / Social) (مساعدة / تعاطف)", value: "ri-hand-heart-line", isClass: true },
  
  // Cognitive & Brain
  { name: "Brain (Cognitive) (دماغ / معرفي)", value: "ri-brain-line", isClass: true },
  { name: "Puzzle (Problem Solving) (أحجية / حل مشكلات)", value: "ri-puzzle-line", isClass: true },
  { name: "Shapes (Geometric learning) (أشكال هندسية)", value: "ri-shapes-line", isClass: true },
  { name: "Lightbulb (Idea / Curiosity) (فكرة / استكشاف)", value: "ri-lightbulb-line", isClass: true },
  { name: "Mind Map (Combining Ideas) (ربط الأفكار)", value: "ri-mind-map", isClass: true },
  { name: "Question Mark (Asking Why?) (سؤال / لماذا؟)", value: "ri-question-line", isClass: true },
  { name: "Chat Help (Exploratory Questions) (أسئلة استكشافية)", value: "ri-chat-help-line", isClass: true },
  { name: "Numbers / Counting (أرقام / عد)", value: "ri-numbers-line", isClass: true },
  { name: "Calculator (Simple Math) (حساب / جمع وطرح)", value: "ri-calculator-line", isClass: true },
  { name: "Book Open (Reading / Stories) (كتاب مفتوح / قصة)", value: "ri-book-open-line", isClass: true },
  { name: "Book Read (Memory / Recall) (قراءة وتذكر)", value: "ri-book-read-line", isClass: true },
  
  // Physical & Motor Skills
  { name: "Running (Jumping / Active) (جري / نشاط حركي)", value: "ri-run-line", isClass: true },
  { name: "Walking (Independent Walk) (مشي مستقل)", value: "ri-walk-line", isClass: true },
  { name: "Footprint (Steps / Crawling) (خطوات / مشي / زحف)", value: "ri-footprint-line", isClass: true },
  { name: "Stairs (Climbing stairs) (صعود الدرج)", value: "ri-stairs-line", isClass: true },
  { name: "Riding (Tricycle / Bike) (ركوب دراجة)", value: "ri-riding-line", isClass: true },
  { name: "Motorbike (Tricycle) (دراجة ثلاثية)", value: "ri-motorbike-line", isClass: true },
  { name: "Body Scan (Posture / Head control) (التحكم بالجسد)", value: "ri-body-scan-line", isClass: true },
  { name: "Shield User (Safety / Balance) (توازن وحماية)", value: "ri-shield-user-line", isClass: true },
  { name: "Hand (Grasping / Reaching) (إمساك بالأيدي)", value: "ri-hand-line", isClass: true },
  { name: "Hand Pointer (Pincer Grasp / Pointing) (إشارة بالسبابة)", value: "ri-hand-pointer-line", isClass: true },
  { name: "Scissors (Cutting / Fine Motor) (مقص / مهارات دقيقة)", value: "ri-scissors-line", isClass: true },
  { name: "Scissors Cut (Pincer / Craft) (قص ولظم)", value: "ri-scissors-cut-line", isClass: true },
  { name: "Node Tree (Threading Beads) (لظم خرز)", value: "ri-node-tree", isClass: true },
  { name: "Links (Connection / Fine Motor) (روابط وخرز)", value: "ri-links-line", isClass: true },
  { name: "T-Shirt (Dressing / Buttoning) (ارتداء الملابس / أزرار)", value: "ri-t-shirt-line", isClass: true },
  { name: "Restaurant / Spoon (Self feeding) (ملعقة وشوكة / تغذية)", value: "ri-restaurant-line", isClass: true },
  
  // Social, Emotional & Play
  { name: "Smile (Social Smiling) (ابتسامة اجتماعية)", value: "ri-smile-line", isClass: true },
  { name: "Happy Face (Joy) (وجه سعيد / مرح)", value: "ri-emotion-happy-line", isClass: true },
  { name: "Sad Face (Empathy / Crying) (حزن / تعاطف / بكاء)", value: "ri-emotion-sad-line", isClass: true },
  { name: "Unhappy Face (Anger / Tantrum) (غضب / نوبات غضب)", value: "ri-emotion-unhappy-line", isClass: true },
  { name: "Laugh (Cooperative Play) (ضحك / لعب جماعي)", value: "ri-emotion-laugh-line", isClass: true },
  { name: "Team (Cooperative play) (لعب تعاوني / فريق)", value: "ri-team-line", isClass: true },
  { name: "Group (Parallel play) (لعب متوازي / أقران)", value: "ri-group-line", isClass: true },
  { name: "Group 2 (Social Interaction) (تفاعل اجتماعي)", value: "ri-group-2-line", isClass: true },
  { name: "Side by Side (Parallel Play) (لعب جنباً إلى جنب)", value: "ri-side-by-side-line", isClass: true },
  { name: "Discuss (Negotiating / Speaking) (تفاوض وتحدث)", value: "ri-discuss-line", isClass: true },
  { name: "Chat Smile (Expressing needs) (تعبير عن الاحتياجات)", value: "ri-chat-smile-line", isClass: true },
  { name: "Chat Voice (Anger with words) (تعبير بالكلام)", value: "ri-chat-voice-line", isClass: true },
  { name: "Voiceprint (Speaking sentences) (تحدث ونطق)", value: "ri-voiceprint-line", isClass: true },
  { name: "Hearts (Friendship / pleasing friends) (صداقة ومحبة)", value: "ri-hearts-line", isClass: true },
  { name: "Crown (Leadership / Team leader) (تاج / قيادة)", value: "ri-vip-crown-line", isClass: true },
  { name: "Trophy (Achievement / Independence) (كأس / إنجاز)", value: "ri-trophy-line", isClass: true },
  { name: "Scales (Fair Play / Justice) (عدالة / لعب عادل)", value: "ri-scales-3-line", isClass: true },
  { name: "Repeat (Taking Turns) (تبادل الأدوار)", value: "ri-repeat-2-line", isClass: true },
  { name: "Exchange (Sharing / Turns) (مشاركة الأدوار)", value: "ri-exchange-line", isClass: true },
  
  // Toys & Objects
  { name: "Teddy Bear (Favorite Toy / Security) (دب لعبة / أمان)", value: "ri-bear-smile-line", isClass: true },
  { name: "Robot (Toy / Tech) (روبوت / لعبة)", value: "ri-robot-line", isClass: true },
  { name: "Gift (Present / Toy) (هدية / لعبة)", value: "ri-gift-line", isClass: true },
  { name: "Gamepad (Interactive Games) (العاب تفاعلية / قيم باد)", value: "ri-gamepad-line", isClass: true },
  { name: "Magic (Fantasy vs Reality) (سحر / خيال)", value: "ri-magic-line", isClass: true },
  { name: "Ghost / Alien (Imaginary Friend / Peekaboo) (صديق خيالي)", value: "ri-ghost-line", isClass: true },
  { name: "Aliens (Imaginary Play) (كائنات فضائية خيالية)", value: "ri-aliens-line", isClass: true },
  { name: "Sparkles (Creativity / Reality) (بريق / خيال)", value: "ri-sparkles-line", isClass: true },
  { name: "Toy Truck (Pushing/Pulling) (شاحنة ألعاب)", value: "ri-truck-line", isClass: true },
  { name: "Shopping Cart (Push/Pull) (عربة دفع وسحب)", value: "ri-shopping-cart-line", isClass: true },
  { name: "Phone (Pretend Phone) (هاتف ألعاب)", value: "ri-phone-line", isClass: true },
  { name: "Door Open (Opening doors) (فتح الأبواب)", value: "ri-door-open-line", isClass: true },
  
  // Perception & Senses
  { name: "Contrast (Color Contrast) (تباين ألوان)", value: "ri-contrast-2-line", isClass: true },
  { name: "Palette (Colors / Drawing) (لوحة ألوان / رسم)", value: "ri-palette-line", isClass: true },
  { name: "Brush (Scribbling / Painting) (فرشاة رسم)", value: "ri-brush-line", isClass: true },
  { name: "Pencil (Drawing / Writing) (قلم رصاص)", value: "ri-pencil-line", isClass: true },
  { name: "Quill Pen (Writing name) (قلم كتابة)", value: "ri-quill-pen-line", isClass: true },
  { name: "Sound Module (Sound linking) (ربط الأصوات)", value: "ri-sound-module-line", isClass: true },
  { name: "Volume (Listening / Hearing) (سمع وصوت)", value: "ri-volume-down-line", isClass: true },
  { name: "Eye (Visual Tracking / Face response) (عين / تتبع بصري)", value: "ri-eye-line", isClass: true },
  { name: "Eye Off (Peek-a-boo) (تغطية العين / الغميضة)", value: "ri-eye-off-line", isClass: true },
  { name: "Search Eye (Object permanence) (بحث واستكشاف)", value: "ri-search-eye-line", isClass: true },
  
  // Rules, Time, Tech & Logic
  { name: "Timer (Task focus) (مؤقت / تركيز)", value: "ri-timer-line", isClass: true },
  { name: "Hourglass (Time duration) (ساعة رملية)", value: "ri-hourglass-line", isClass: true },
  { name: "Time / Clock (Time sequence) (وقت / تسلسل زمني)", value: "ri-time-line", isClass: true },
  { name: "Sun & Cloud (Morning / Time concept) (صباح ومساء)", value: "ri-sun-cloudy-line", isClass: true },
  { name: "Moon (Evening / Night concept) (ليل ومساء)", value: "ri-moon-line", isClass: true },
  { name: "Compass (Directions / Right & Left) (بوصلة / اتجاهات)", value: "ri-compass-3-line", isClass: true },
  { name: "Navigation (Directions / Path) (توجيه / يمين ويسار)", value: "ri-navigation-line", isClass: true },
  { name: "Close Circle (Understanding 'No') (دائرة الإغلاق / 'لا')", value: "ri-close-circle-line", isClass: true },
  { name: "Printer (3D Printing / Tech) (طابعة ثلاثية الأبعاد)", value: "ri-printer-line", isClass: true },
  { name: "Box 3D (Geometric / Shapes) (شكل ثلاثي الأبعاد)", value: "ri-box-3-line", isClass: true },
  { name: "Tablet / iPad (Tech tools) (تابلت / أجهزة تقنية)", value: "ri-tablet-line", isClass: true },
  { name: "Computer (Tech skills) (كمبيوتر / تقنية)", value: "ri-computer-line", isClass: true },
];

// Dynamically extract all available Tabler icons from the package
const tablerList: IconItem[] = Object.keys(TablerIcons)
  .filter((key) => key.startsWith("Icon"))
  .map((key) => {
    const name = key.replace(/^Icon/, "");
    return {
      name: `${name} (Tabler)`,
      value: `icon:${name}`,
      component: (TablerIcons as any)[key],
    };
  });

// Dynamically extract all available Lucide icons from the package
const lucideList: IconItem[] = Object.keys(LucideIcons)
  .filter((key) => /^[A-Z]/.test(key) && key !== "createReactComponent" && typeof (LucideIcons as any)[key] !== "undefined")
  .map((key) => {
    return {
      name: `${key} (Lucide)`,
      value: `lucide:${key}`,
      component: (LucideIcons as any)[key],
    };
  });

export const iconLibrary: IconItem[] = [
  ...childDevIcons,
  ...tablerList,
  ...lucideList,
].sort((a, b) => a.name.localeCompare(b.name));

export const getTablerIconByName = (name: string) => {
  const key = `Icon${name}`;
  return (TablerIcons as any)[key] || null;
};

export const getLucideIconByName = (name: string) => {
  return (LucideIcons as any)[name] || null;
};

type MediaPickerProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
};

type MediaMode = "icon" | "svg" | "image";

type MediaItem = {
  name: string;
  type: "svg" | "image" | "other";
  url: string;
};

const isImageUrl = (value: string) => {
  const lower = value.toLowerCase();
  return lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".gif") || lower.endsWith(".webp");
};

const isSvgUrl = (value: string) => value.toLowerCase().endsWith(".svg");

export default function MediaPicker({ value, onChange, label }: MediaPickerProps) {
  const { t } = useTranslation("common");
  const [mode, setMode] = useState<MediaMode>("icon");
  const [iconCategory, setIconCategory] = useState<"child-dev" | "tabler" | "lucide" | "all">("child-dev");
  const [items, setItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const selectedIcon = useMemo(() => iconLibrary.find((icon) => icon.value === value), [value]);

  // Reset search when mode changes
  useEffect(() => {
    setSearchQuery("");
  }, [mode]);

  useEffect(() => {
    if (mode === "icon") return;

    setIsLoading(true);
    setError("");
    fetchAdminMedia(mode)
      .then((data) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setIsLoading(false));
  }, [mode]);

  const handleUpload = async (file: File) => {
    setError("");
    try {
      const uploaded = await uploadAdminMedia(file);
      onChange(uploaded.url);
      setItems((prev) => [uploaded, ...prev]);
    } catch {
      setError(t("adminMediaUploadFailed"));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    void handleUpload(file);
  };

  // Search filter for Icons based on category and query
  const filteredIcons = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    
    // First filter by category
    let list = iconLibrary;
    if (iconCategory === "child-dev") {
      list = iconLibrary.filter((icon) => icon.isClass);
    } else if (iconCategory === "tabler") {
      list = iconLibrary.filter((icon) => icon.value.startsWith("icon:"));
    } else if (iconCategory === "lucide") {
      list = iconLibrary.filter((icon) => icon.value.startsWith("lucide:"));
    }

    if (!query) return list;
    return list.filter((icon) => icon.name.toLowerCase().includes(query));
  }, [iconCategory, searchQuery]);

  // Limit rendering for performance
  const visibleIcons = useMemo(() => {
    return filteredIcons.slice(0, 180);
  }, [filteredIcons]);

  // Search filter for SVGs/Images
  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [items, searchQuery]);

  const accept = mode === "svg" ? ".svg" : "image/*";

  return (
    <div className="space-y-4">
      {label && <label className="block text-xs font-semibold text-gray-500 dark:text-zinc-400">{label}</label>}

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-gray-50 dark:bg-zinc-800 p-1.5 rounded-xl border border-gray-100 dark:border-zinc-700 w-max">
        <button
          type="button"
          onClick={() => setMode("icon")}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
            mode === "icon" 
              ? "bg-white dark:bg-zinc-700 text-teal-600 dark:text-teal-400 shadow-sm" 
              : "text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-200 hover:bg-gray-100/50 dark:hover:bg-zinc-700/50"
          }`}
        >
          {t("adminMediaTabIcon")}
        </button>
        <button
          type="button"
          onClick={() => setMode("svg")}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
            mode === "svg" 
              ? "bg-white dark:bg-zinc-700 text-teal-600 dark:text-teal-400 shadow-sm" 
              : "text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-200 hover:bg-gray-100/50 dark:hover:bg-zinc-700/50"
          }`}
        >
          {t("adminMediaTabSvg")}
        </button>
        <button
          type="button"
          onClick={() => setMode("image")}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
            mode === "image" 
              ? "bg-white dark:bg-zinc-700 text-teal-600 dark:text-teal-400 shadow-sm" 
              : "text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-200 hover:bg-gray-100/50 dark:hover:bg-zinc-700/50"
          }`}
        >
          {t("adminMediaTabImage")}
        </button>
      </div>

      {/* Search Input and Category Dropdown */}
      <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-zinc-500 text-base" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("adminMediaSearchPlaceholder")}
            className="w-full pl-9 pr-4 rtl:pl-4 rtl:pr-9 py-2.5 border border-gray-200 dark:border-zinc-700 rounded-xl text-sm bg-white dark:bg-zinc-900 text-gray-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all shadow-sm placeholder-gray-400 dark:placeholder-zinc-500"
          />
        </div>

        {mode === "icon" && (
          <div className="relative min-w-[220px]">
            <select
              value={iconCategory}
              onChange={(e) => setIconCategory(e.target.value as any)}
              className="w-full pl-3 pr-8 rtl:pl-8 rtl:pr-3 py-2.5 border border-gray-200 dark:border-zinc-700 rounded-xl text-sm bg-white dark:bg-zinc-900 text-gray-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all shadow-sm cursor-pointer appearance-none font-medium"
            >
              <option value="child-dev">{t("adminMediaCategoryChildDev")}</option>
              <option value="tabler">{t("adminMediaCategoryTabler")}</option>
              <option value="lucide">{t("adminMediaCategoryLucide")}</option>
              <option value="all">{t("adminMediaCategoryAll")}</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 rtl:right-auto rtl:left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-zinc-500 text-lg" />
          </div>
        )}
      </div>

      {/* Icons Grid with Scrollable Viewport */}
      {mode === "icon" && (
        <div className="space-y-2">
          <div className="max-h-80 overflow-y-auto pr-2 border border-gray-150 dark:border-zinc-800 rounded-xl p-3 bg-white dark:bg-zinc-900/40">
            {visibleIcons.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-zinc-500 text-center py-8">{t("adminMediaNoIcons")}</p>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                {visibleIcons.map((icon) => {
                  const IconComponent = icon.component;
                  const isSelected = value === icon.value;
                  return (
                    <button
                      key={icon.value}
                      type="button"
                      onClick={() => onChange(icon.value)}
                      className={`border rounded-xl p-3 flex flex-col items-center gap-2 transition-all ${
                        isSelected 
                          ? "border-teal-500 bg-teal-50/50 dark:bg-teal-950/20 text-teal-700 dark:text-teal-400 shadow-sm ring-2 ring-teal-100 dark:ring-teal-900/30" 
                          : "border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-600 dark:text-zinc-400 hover:bg-gray-50 dark:hover:bg-zinc-800/80 hover:border-gray-300 dark:hover:border-zinc-700"
                      }`}
                    >
                      {icon.isClass ? (
                        <div className="h-6 flex items-center justify-center text-teal-600 dark:text-teal-400">
                          <i className={`${icon.value} text-2xl`} />
                        </div>
                      ) : (
                        <IconComponent size={24} />
                      )}
                      <span className="text-[10px] font-semibold truncate w-full text-center">{icon.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {filteredIcons.length > visibleIcons.length && (
            <p className="text-[10px] text-gray-400 dark:text-zinc-500 px-1 font-medium">
              {t("adminMediaShowingIcons", { visible: visibleIcons.length, total: filteredIcons.length })}
            </p>
          )}
        </div>
      )}

      {/* SVG/Image Mode */}
      {mode !== "icon" && (
        <div className="space-y-4 p-4 border border-dashed border-gray-300 dark:border-zinc-750 rounded-xl bg-gray-50/50 dark:bg-zinc-900/30">
          <div className="flex items-center justify-center">
            <input 
              type="file" 
              accept={accept} 
              onChange={handleFileChange} 
              className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 dark:file:bg-teal-950/30 file:text-teal-700 dark:file:text-teal-400 hover:file:bg-teal-100 dark:hover:file:bg-teal-900/40 transition-all cursor-pointer"
            />
          </div>

          {error && <p className="text-xs text-red-500 text-center">{error}</p>}

          {isLoading ? (
            <p className="text-xs text-gray-400 dark:text-zinc-500 text-center">{t("loading")}</p>
          ) : filteredItems.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-zinc-500 text-center">{t("adminMediaEmpty")}</p>
          ) : (
            <div className="max-h-80 overflow-y-auto pr-2">
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                {filteredItems.map((item) => (
                  <button
                    key={item.url}
                    type="button"
                    onClick={() => onChange(item.url)}
                    className={`border rounded-xl p-2 flex items-center justify-center aspect-square transition-all ${
                      value === item.url 
                        ? "border-teal-500 ring-2 ring-teal-100 dark:ring-teal-900/30 bg-white dark:bg-zinc-800" 
                        : "border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-gray-300 dark:hover:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800"
                    }`}
                  >
                    <img src={item.url} alt={item.name} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Selected Preview */}
      {value && (
        <div className="flex items-center gap-3 p-3 bg-teal-50/50 dark:bg-teal-950/10 border border-teal-100 dark:border-teal-900/20 rounded-xl mt-4">
          <span className="text-xs font-bold text-teal-800 dark:text-teal-400">{t("adminMediaSelected")}</span>
          <div className="w-10 h-10 bg-white dark:bg-zinc-900 rounded-lg border border-teal-100 dark:border-teal-900/30 flex items-center justify-center shadow-sm text-teal-600 dark:text-teal-400">
            {selectedIcon ? (
              selectedIcon.isClass ? (
                <i className={`${selectedIcon.value} text-2xl`} />
              ) : (
                <selectedIcon.component size={24} />
              )
            ) : value.startsWith("ri-") ? (
              <i className={`${value} text-2xl`} />
            ) : isSvgUrl(value) || isImageUrl(value) ? (
              <img src={value} alt="selected" className="w-8 h-8 object-contain" />
            ) : (
              <span className="text-xs text-gray-400 truncate max-w-full px-1">{value}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
